import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * The reconcile / "view details" side panel for a bank transaction.
 *
 * One component for both hosts: the Transactions ledger and the transaction grid
 * inside a report (ReportTransactionsComponent). Before this, the report grid had a
 * hand-rolled fixed div driven by an `isBankTransMode` boolean with no scrim, and the
 * new Transactions screen would have needed a second copy of it.
 *
 * SCOPE, and the reason it is this small: the panel's paging ("1 of 1" with the two
 * arrows) and its Details / Activity tabs are NOT here. ActivityComponent already
 * renders both when `isBankTransMode` is set, and pages through `bankTransArr` with
 * its own `curIdx` -- see activity.component.html around the `.tab-header` block.
 * Reproducing them here would put two sets of arrows and two tab rows on screen.
 *
 * So this owns exactly what was duplicated and nothing else: the scrim, the panel
 * surface, and the close affordance. The body is projected, which also means
 * ActivityComponent can stay in BookkeepingModule rather than being hoisted into
 * CommonSharedModule and risking a circular import.
 *
 * Hosts with footer actions project them into the [panelFooter] slot.
 */
@Component({
  selector: 'app-transaction-detail-panel',
  templateUrl: './transaction-detail-panel.component.html',
  styleUrls: ['./transaction-detail-panel.component.scss']
})
export class TransactionDetailPanelComponent {

  @Input() open = false;

  /** For screen readers; hosts can name the transaction when they know it. */
  @Input() label = 'Transaction details';

  /**
   * Raised above the invoice form panel when a host stacks the two -- the report grid
   * does exactly this today via `pageSidebarComponent.showInvoiceFormPanel`.
   */
  @Input() elevated = false;

  /**
   * Whether to render the [panelFooter] slot. Explicit rather than inferred: an empty
   * <ng-content> still produces an element, so an always-on footer would draw a bare
   * rule across the bottom of every panel that has no actions.
   */
  @Input() showFooter = false;

  @Output() closed = new EventEmitter<void>();

  close(): void {
    this.closed.emit();
  }
}
